// ============================================================================
// instancesNav.js — SOURCE UNIQUE des quatre pages d'instance (Gouvernance)
// ============================================================================
//
// Refonte « approche en cascade » (décision Pierre 04/09/2026) : le menu
// Gouvernance liste UNE entrée par instance — Assemblée générale, Conseil
// d'administration, Comité de direction, Bureau — et chaque page porte des
// rubriques (Séances, Documents, Composition, Mandats) qui sont de vraies
// routes : `gouvernance-<instance>` (= Séances, l'atterrissage) et
// `gouvernance-<instance>-<rubrique>`.
//
// Avant, la liste des instances vivait à quatre endroits (navConstants,
// GouvernancePanel ×2, App.jsx) et le mot « Instances » menait à trois pages
// différentes. Ici : identité, routes, correspondances avec les types de
// séance Thémis (`sessionType`) et de réunion Outlook (`reunionType`), et le
// périmètre d'accès documentaire (`scope`, cf. src/accesInstances.js). La
// config MÉTIER (rôles, dossiers SharePoint, sièges) reste INSTANCES_CFG dans
// src/GouvernanceInstance.js, référencée par `cfgKey`.
//
// Ce module est importé par navConstants : il ne doit rien importer de lourd
// (pas de React, pas de Firebase) — les helpers y sont purs et testés
// (src/instancesNav.test.js).
// ============================================================================

// `icon` : clé de src/icones.js. Les quatre instances portent le MÊME losange
// plein : c'est leur sous-teinte `couleur` qui les distingue (AG bleu, CA teal,
// CODIR violet, Bureau orange — synthèse Mosaïque §4.1 et §4.5, lot L3). Toute
// autre table de couleurs d'instance DOIT dériver de celle-ci (appShared.js
// INSTANCE_TYPE_COLORS, src/identiteModules.test.js).
//
// `signatairesConvocation` : les postes qui signent la convocation (document
// ET corps du mail) quand la séance n'a coché AUCUN signataire, dans l'ordre
// des colonnes. Codes de poste : "president" (gouvernance.postesActifs) et
// "dg" (gouvernance.orgEnio.dg). Le CoDIR est le comité du Directeur
// général : lui seul signe (retour de Laurence du 28/09/2026, « ce devrait
// être uniquement la signature du DG », confirmé par Pierre). Les noms sont
// résolus par src/signatairesConvocation.js, jamais écrits ici.
//
// `deliberation` : comment l'instance décide (SOURCE UNIQUE, src/codirSansQuorum.test.js).
//   "statutaire" : quorum et pouvoirs selon les statuts (AG, CA) ;
//   "sansQuorum" : le CODIR, comité du Directeur général. Il ne délibère pas
//                  sous quorum : ni règle ni calcul de quorum, ni pouvoirs
//                  (demande de Pierre du 02/10/2026, « des règles de quorum
//                  qui ne servent à rien »). Un vote éventuel s'y compte à la
//                  majorité des suffrages exprimés, sans condition de quorum ;
//   "informelle" : le Bureau, ni vote, ni quorum, ni pouvoirs (décision de
//                  Pierre du 27/08/2026).
// Les autres types de séance (comité « CO », séminaire « SE », vote libre
// « VL », consultation « CS ») gardent leur comportement historique.
//
// `document` : le document de séance (SOURCE UNIQUE, lue par src/documentSeance.js,
// miroir serveur functions/lib/documentSeance.js ; demande de Laurence BLANCHE
// approuvée par Pierre le 20/10/2026) :
//   nature      : "proces-verbal" (AG, CA) ou "compte-rendu" (toute autre
//                 instance) : titre, mentions du corps, nom du fichier, écrans ;
//   decision    : ce que l'instance adopte : "resolution" (AG),
//                 "deliberation" (CA), null pour une instance qui ne délibère
//                 pas (CODIR, Bureau : aucune formule d'adoption ni de
//                 délibération au compte rendu) ;
//   signataires : postes qui signent le document ("president" et "sg" de
//                 gouvernance.postesActifs, "dg" de gouvernance.orgEnio.dg) ;
//                 les noms sont lus sur les fiches, jamais écrits ici.
// Un type hors des quatre (séminaire, comité, vote libre…) : compte rendu,
// signé du Directeur général, ses votes gardant leur forme historique
// (DOCUMENT_REPLI de src/documentSeance.js).
//
// `reperesNom` : ce que le NOM d'une séance dit de son instance, lu en TÊTE du
// nom seulement (« CODIR du 7 octobre », « Réunion du Comité de direction ») :
// « Élection du Bureau » ou « Conseil : présentation du CODIR » ne désignent
// pas l'instance qui siège. Sigle en capitales : « CA » ne se confond pas avec
// « ça ». Il ne décide jamais du type : il fonde seulement la proposition de
// correction faite à l'administrateur (typeEffectifSeance), parce qu'une
// séance créée depuis le hub naissait « CA » quoi qu'on veuille préparer.
export const INSTANCES_NAV = [
  { key: "ag",     route: "gouvernance-ag",     label: "Assemblée générale : délégués", court: "AG",     icon: "losange", couleur: "#2AA9E1", scope: "AG",     sessionType: "AG", reunionType: "AG", cfgKey: "ag",
    signatairesConvocation: ["president", "dg"], deliberation: "statutaire",
    document: { nature: "proces-verbal", decision: "resolution", signataires: ["president", "sg"] },
    reperesNom: [/^AG[EO]?(?![\p{L}\d])/u, /^assembl[ée]e\s+g[ée]n[ée]rale/i],
    description: "Délégués, séances de l'AG et leurs documents." },
  { key: "ca",     route: "gouvernance-ca",     label: "Conseil d'administration",      court: "CA",     icon: "losange", couleur: "#1D948A", scope: "CA",     sessionType: "CA", reunionType: "CA", cfgKey: "ca",
    signatairesConvocation: ["president", "dg"], deliberation: "statutaire",
    document: { nature: "proces-verbal", decision: "deliberation", signataires: ["president", "sg"] },
    reperesNom: [/^CA(?![\p{L}\d])/u, /^conseil\s+d['’]\s*administration/i],
    description: "Administrateurs, séances du CA et leurs documents." },
  { key: "codir",  route: "gouvernance-codir",  label: "Comité de direction",           court: "CODIR",  icon: "losange", couleur: "#951B81", scope: "CODIR",  sessionType: "CD", reunionType: "CD", cfgKey: "codir",
    signatairesConvocation: ["dg"], deliberation: "sansQuorum",
    document: { nature: "compte-rendu", decision: null, signataires: ["dg"] },
    reperesNom: [/^co-?dir(?![\p{L}\d])/iu, /^comit[ée]\s+de\s+direction/i],
    description: "Membres du CODIR, séances et documents." },
  { key: "bureau", route: "gouvernance-bureau", label: "Bureau",                        court: "Bureau", icon: "losange", couleur: "#F49800", scope: "BUREAU", sessionType: "BU", reunionType: null, cfgKey: "bureau",
    signatairesConvocation: ["president", "dg"], deliberation: "informelle",
    // Compte rendu du Bureau signé du Président SEUL (décision de Pierre du 03/11/2026).
    document: { nature: "compte-rendu", decision: null, signataires: ["president"] },
    reperesNom: [/^bureau(?![\p{L}\d])/iu],
    description: "Titulaires des postes, séances du Bureau et documents." },
];

// Rubriques d'une page d'instance. `seances` est l'atterrissage (suffixe vide).
// `mandats` n'existe pas pour le CODIR (désignation libre, pas de mandature).
export const SECTIONS_INSTANCE = [
  { key: "seances",     label: "Séances",     icon: "calendar", suffixe: "" },
  { key: "documents",   label: "Documents",   icon: "folder", suffixe: "-documents" },
  { key: "composition", label: "Composition", icon: "users", suffixe: "-composition" },
  { key: "mandats",     label: "Mandats",     icon: "calendar-time", suffixe: "-mandats" },
];

// Pages transverses (hors instance) — routes historiques conservées.
export const TRANSVERSES_GOUVERNANCE = {
  mandats: "gouvernance-mandats",
  organigramme: "gouvernance-organigramme",
  documents: "gouvernance-documents",
};

export const instanceParKey = (key) => INSTANCES_NAV.find((i) => i.key === key) || null;

export const sectionsPour = (inst) =>
  SECTIONS_INSTANCE.filter((s) => !(s.key === "mandats" && inst && inst.key === "codir"));

export function routeInstance(key, section = "seances") {
  const inst = instanceParKey(key);
  if (!inst) return "gouvernance";
  const sec = SECTIONS_INSTANCE.find((s) => s.key === section) || SECTIONS_INSTANCE[0];
  return inst.route + sec.suffixe;
}

/** Toutes les vues du module Gouvernance portées par ce fichier. */
export const GOUVERNANCE_VIEWS = [
  "gouvernance",
  ...Object.values(TRANSVERSES_GOUVERNANCE),
  ...INSTANCES_NAV.flatMap((i) => sectionsPour(i).map((s) => routeInstance(i.key, s.key))),
];

/**
 * `gouvernance` → ensemble ; `gouvernance-mandats|organigramme|documents` →
 * transverse ; `gouvernance-<inst>[-<rubrique>]` → instance. Une rubrique
 * inconnue retombe sur Séances, une instance inconnue sur la vue d'ensemble :
 * jamais de page blanche. Aucune collision : le premier segment est soit une
 * clé d'instance, soit une clé transverse.
 */
export function parseRouteGouvernance(view) {
  const v = String(view || "");
  if (v === "gouvernance") return { kind: "ensemble" };
  if (!v.startsWith("gouvernance-")) return { kind: "ensemble" };
  const reste = v.slice("gouvernance-".length);
  const [premier, ...suite] = reste.split("-");
  if (TRANSVERSES_GOUVERNANCE[premier] && suite.length === 0) return { kind: "transverse", transverse: premier };
  const inst = instanceParKey(premier);
  if (!inst) return { kind: "ensemble" };
  const sec = suite.length ? suite.join("-") : "seances";
  const valide = sectionsPour(inst).some((s) => s.key === sec);
  return { kind: "instance", instance: inst.key, section: valide ? sec : "seances" };
}

// Écritures d'un type de séance rencontrées en base et dans le code : le code
// (« CD »), le nom court (« CODIR »), la clé de page (« codir »), le périmètre
// documentaire, le libellé (« Comité de direction »), sans casse ni accents.
// Toutes DÉRIVENT de INSTANCES_NAV : la table d'alias recopiée à la main
// (CODIR, Bureau) oubliait « Codir », « codir » ou « Comité de direction ».
const cleAlias = (t) => String(t || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "")
  .replace(/[’]/g, "'").replace(/\s+/g, " ").trim().toUpperCase();
const ALIAS_TYPE_SEANCE = new Map(INSTANCES_NAV.flatMap((i) =>
  [i.sessionType, i.court, i.key, i.cfgKey, i.scope, i.label].filter(Boolean).map((a) => [cleAlias(a), i.sessionType])));

/** Le code canonique d'un type de séance (« CODIR », « codir », « Comité de
 *  direction » → « CD » ; « Bureau » → « BU ») ; un autre type ressort en
 *  capitales (« SE », « VL »…), une absence en chaîne vide. */
export const typeSeance = (t) => {
  const cle = cleAlias(t);
  return ALIAS_TYPE_SEANCE.get(cle) || cle;
};
const normType = typeSeance;

export const instanceParSessionType = (t) => INSTANCES_NAV.find((i) => i.sessionType === normType(t)) || null;
export const instanceParReunionType = (t) => INSTANCES_NAV.find((i) => i.reunionType && i.reunionType === normType(t)) || null;

/** Le nom court affiché sur les pastilles (« CODIR », « Bureau », « CA »…),
 *  le code pour un type hors des quatre instances, "" sans type. */
export const courtDeType = (t) => {
  const code = typeSeance(t);
  const inst = instanceParSessionType(code);
  return inst ? inst.court : code;
};

// ── Délibération : quorum et pouvoirs selon l'instance ──────────────────────
/** "statutaire" | "sansQuorum" | "informelle", ou null hors des quatre
 *  instances (comité, séminaire, vote libre : comportement historique). */
export const deliberationDe = (t) => (instanceParSessionType(t) || {}).deliberation || null;
/** Le Bureau : ni vote, ni quorum, ni pouvoirs. */
export const estDeliberationInformelle = (t) => deliberationDe(t) === "informelle";
/** CODIR et Bureau : aucune règle de quorum, aucun calcul de quorum. */
export const estSansQuorum = (t) => ["sansQuorum", "informelle"].includes(deliberationDe(t));
/** CODIR et Bureau : aucun pouvoir, quel que soit un plafond posé sur la séance. */
export const estSansPouvoirs = (t) => ["sansQuorum", "informelle"].includes(deliberationDe(t));

/** La phrase sobre que les écrans affichent à la place d'un compteur de
 *  quorum (vide pour une instance qui délibère sous quorum). */
export function phraseSansQuorum(t) {
  const d = deliberationDe(t);
  if (d === "sansQuorum") return "Le Comité de direction ne délibère pas sous quorum : ni quorum, ni pouvoirs.";
  if (d === "informelle") return "Instance informelle : ni vote, ni quorum, ni pouvoirs.";
  return "";
}

/** Le libellé de l'étape de suivi des réponses : il n'annonce ni quorum ni
 *  pouvoirs pour une instance qui n'en a pas (CODIR, Bureau). */
export const libelleEtapeSuivi = (t) => (estSansQuorum(t) ? "Participants et réponses" : "Participants, quorum et pouvoirs");

// Repères STRUCTURELS d'une séance : écrits par un chemin qui connaît
// l'instance (clé de page, clé de configuration, périmètre documentaire).
const REPERES_INSTANCE = ["instanceKey", "instance", "cfgKey", "scope"];
// Les types qu'une séance recevait SANS qu'on les choisisse : aucun, « CA »
// (bouton « Nouvelle session » du hub, jusqu'au 02/10/2026) et « AG »
// (formulaire historique de l'onglet Thémis).
const TYPES_PAR_DEFAUT = ["", "CA", "AG"];
// Ce qui précède l'instance en tête d'un nom (« Réunion du CODIR », « Séance
// du Conseil d'administration ») : retiré avant de lire les repères du nom.
const AMORCE_NOM = /^\s*(?:(?:r[ée]union|s[ée]ance)\s+(?:du\s+|de\s+la\s+|de\s+l['’]\s*|d['’]\s*)?)?/i;

/**
 * Le type EFFECTIF d'une séance, et ce qu'il faut dire à l'administrateur.
 *   • `type`      : le type que les calculs doivent suivre ;
 *   • `declare`   : le type enregistré (alias résolus) ;
 *   • `reconnu`   : vrai quand des repères structurels UNANIMES (instanceKey,
 *                   instance, cfgKey, scope) désignent une autre instance que
 *                   le type enregistré : ils l'emportent, sans rien écrire ;
 *   • `repere`    : le champ qui l'a établi ;
 *   • `suggestion`: { type, instance } quand seul le NOM, lu en tête,
 *                   désigne une autre instance que le type enregistré, et que
 *                   ce type est un défaut silencieux. Rien ne change sans le
 *                   geste explicite de l'administrateur.
 * Des repères qui se contredisent ne décident rien : le type enregistré reste.
 * Un type CONFIRMÉ par l'administrateur (`instanceTypeVerifie` : choisi à la
 * création, au Cadre, ou gardé sur l'encart « Type d'instance à vérifier »)
 * l'emporte sur tout : ni reconnaissance, ni proposition.
 *
 * Portée de la reconnaissance : l'écran et les calculs (quorum, voix,
 * pouvoirs, étapes, ordre du jour, PV, appel en séance) suivent `type` ; ce
 * qui s'écrit ou part (convocation, signataires, validation, dossier
 * SharePoint, invitation Outlook, gardes du serveur) suit le type ENREGISTRÉ.
 */
export function typeEffectifSeance(sess) {
  const declare = typeSeance(sess && sess.instanceType);
  const vu = { type: declare, declare, reconnu: false, repere: "", suggestion: null };
  if (!sess) return vu;
  if (declare && typeSeance(sess.instanceTypeVerifie) === declare) return vu;
  const parRepere = new Map();
  for (const champ of REPERES_INSTANCE) {
    const v = sess[champ];
    if (typeof v !== "string" || !v.trim()) continue;
    const inst = instanceParSessionType(v);
    if (inst && !parRepere.has(inst.sessionType)) parRepere.set(inst.sessionType, champ);
  }
  if (parRepere.size === 1) {
    const [type, champ] = [...parRepere][0];
    return type === declare ? vu : { ...vu, type, reconnu: true, repere: champ };
  }
  if (parRepere.size === 0 && TYPES_PAR_DEFAUT.includes(declare)) {
    const nom = String(sess.name || "").replace(AMORCE_NOM, "");
    const indices = INSTANCES_NAV.filter((i) => (i.reperesNom || []).some((re) => re.test(nom)));
    if (indices.length === 1 && indices[0].sessionType !== declare) {
      vu.suggestion = { type: indices[0].sessionType, instance: indices[0] };
    }
  }
  return vu;
}

/** Raccourci : le type que les calculs d'une séance doivent suivre. */
export const typeDeSeance = (sess) => typeEffectifSeance(sess).type;

// Repli pour un type de séance hors des quatre pages d'instance (séminaire
// « SE », comité « CO »…) : la règle historique, Président + Directeur
// général — celle qui s'appliquait à TOUTES les instances avant le retour de
// Laurence du 28/09/2026.
export const SIGNATAIRES_CONVOCATION_REPLI = Object.freeze(["president", "dg"]);

/** Postes signataires PAR DÉFAUT de la convocation d'un type de séance
 *  (alias historiques compris : « CODIR » = « CD »). */
export const postesSignatairesConvocation = (sessionType) => {
  const inst = instanceParSessionType(sessionType);
  return (inst && Array.isArray(inst.signatairesConvocation) && inst.signatairesConvocation.length)
    ? inst.signatairesConvocation : SIGNATAIRES_CONVOCATION_REPLI;
};

/** Date ISO d'une séance : dateISO (source de vérité) puis date JJ/MM/AAAA. */
export function dateISOSeance(sess) {
  if (!sess) return "";
  if (/^\d{4}-\d{2}-\d{2}/.test(sess.dateISO || "")) return String(sess.dateISO).slice(0, 10);
  const m = /^(\d{2})\/(\d{2})\/(\d{4})/.exec(sess.date || "");
  if (m) return `${m[3]}-${m[2]}-${m[1]}`;
  if (/^\d{4}-\d{2}-\d{2}/.test(sess.date || "")) return String(sess.date).slice(0, 10);
  return "";
}

/**
 * Séances d'une instance depuis `sessions/current` (appState) : la séance
 * LIVE (participants/agenda à la RACINE du doc, cf. src/themisLifecycle.js),
 * les séances en préparation et l'historique (deux formats : session archivée
 * moderne, ou lignes legacy portant `session`). Dédoublonné par id.
 * `aVenir` : date ≥ aujourd'hui ou sans date (brouillons, en fin de liste),
 * croissant ; `passees` : décroissant.
 */
export function seancesParInstance(appState, sessionType, todayISO = new Date().toISOString().slice(0, 10)) {
  const type = normType(sessionType);
  const st = appState || {};
  const vus = new Set();
  const rows = [];
  const pousser = (sess, state, extra = {}) => {
    if (!sess || !sess.id || vus.has(sess.id)) return;
    // Le type EFFECTIF (alias et repères de la séance) : la même lecture que
    // la préparation, pour que la séance soit rangée là où on la prépare.
    if (typeDeSeance(sess) !== type) return;
    vus.add(sess.id);
    rows.push({
      sess, state,
      participants: extra.participants || sess.participants || [],
      agenda: extra.agenda || sess.agenda || [],
      dateISO: dateISOSeance(sess),
    });
  };
  if (st.session) pousser(st.session, "active", { participants: st.participants || [], agenda: st.agenda || [] });
  for (const p of (st.pendingSessions || [])) pousser(p, "pending");
  for (const h of (st.history || [])) {
    if (!h) continue;
    const id = h.id || h.sessionId || (h.session && h.session.id);
    const base = h.instanceType ? h : (h.session ? { ...h.session, ...h, id } : null);
    if (!base) continue;
    pousser({ ...base, id }, "history");
  }
  const aVenir = rows.filter((r) => r.state !== "history" && (!r.dateISO || r.dateISO >= todayISO))
    .sort((a, b) => (a.dateISO && b.dateISO) ? a.dateISO.localeCompare(b.dateISO) : (a.dateISO ? -1 : b.dateISO ? 1 : 0));
  const passees = rows.filter((r) => r.state === "history" || (r.dateISO && r.dateISO < todayISO))
    .sort((a, b) => (b.dateISO || "").localeCompare(a.dateISO || ""));
  return { aVenir, passees };
}

/**
 * La dernière séance RÉUNIE (clôturée, dans l'historique) d'une instance
 * avant une date ISO : celle dont le procès-verbal ou le compte rendu
 * s'approuve à la séance suivante (repère [date_pv] des modèles, demande de
 * Pierre du 21/10/2026 : « CA pour un CA, AG pour une AG, CODIR pour un
 * CODIR »). null sans séance réunie datée.
 */
export function seanceReunieAvant(appState, sessionType, dateISO = "") {
  const { passees } = seancesParInstance(appState, sessionType, dateISO || undefined);
  return passees.find((r) => r.state === "history" && r.dateISO && (!dateISO || r.dateISO < dateISO)) || null;
}

/** La prochaine séance (non clôturée) d'une instance à partir d'une date
 *  ISO (repère [date AG] : l'AG à laquelle un CA soumet ses projets). */
export function prochaineSeance(appState, sessionType, dateISO = "") {
  const { aVenir } = seancesParInstance(appState, sessionType, dateISO || undefined);
  return aVenir.find((r) => r.dateISO && (!dateISO || r.dateISO >= dateISO)) || null;
}

const normEmail = (e) => String(e || "").normalize("NFC").toLowerCase().replace(/\s+/g, "").trim();

/**
 * Pré-contrôle FRONT du guichet documentaire : l'appelant est-il parmi les
 * participants de la séance ? Miroir de peutVoirDocsSeance
 * (functions/sharepoint.js) sans les drapeaux — le DROIT reste au serveur.
 */
export function estParticipant(participants, session) {
  return !!participantDansSeance(participants, session);
}

/**
 * La LIGNE de participant de la personne connectée (même rapprochement que
 * estParticipant : id, delegateId, e-mail et alias), ou null. Sert « Ma
 * semaine » (lot L7) : sa réponse et son pouvoir se lisent sur SA ligne.
 */
export function participantDansSeance(participants, session) {
  if (!session) return null;
  const ids = new Set([session.id].filter(Boolean));
  const emails = new Set([session.email, session.mail, ...(Array.isArray(session.emailAliases) ? session.emailAliases : [])]
    .map(normEmail).filter(Boolean));
  return (participants || []).find((p) => p && (
    (p.id && ids.has(p.id)) || (p.delegateId && ids.has(p.delegateId)) || (p.email && emails.has(normEmail(p.email))))) || null;
}
